import type { Marathon } from '@/api/courses';
import type { TransferReport } from '@/api/enrollmentTransfer';

import { apiErrorCode } from './apiErrorCode';

/**
 * Решения диалога «Перенести на другой курс» — без разметки, чтобы их можно было проверить.
 *
 * Договор — `docs/CONTRACTS/enrollment-transfer.md` §3 (условия) и §9 (ручки и коды отказов).
 */

/** Запись ученика — ровно то, что нужно решениям: курс и статус. */
type EnrollmentLite = { marathon_id: string; status: string };

/** Цель переноса в выпадающем списке: курс того же подтипа и готов ли он принять ученика. */
export type TransferTarget = {
  id: string;
  title: string;
  /** Активная запись ученика в этом курсе есть — перенос возможен. Нет — сервер ответит `no_target_enrollment`. */
  hasEnrollment: boolean;
};

/**
 * Курсы, куда можно перенести ученика из `fromMarathonId`: тот же НЕПУСТОЙ подтип, не сам источник, не образец.
 *
 * ⚠️ КУРС БЕЗ ЗАПИСИ УЧЕНИКА В СПИСКЕ ОСТАЁТСЯ, НО НЕАКТИВЕН. Перенос запись в цели не создаёт (§3: срок, тариф и
 * права выбирает человек штатной «Выдать доступ») — скрой такой курс, и админ не понял бы, почему нужного курса нет.
 *
 * Пустой подтип — целей нет: сервер откажет `subtype_mismatch`, пары по `mirror_key` у курсов разных подтипов нет.
 */
export function transferTargets(
  marathons: readonly Marathon[],
  enrollments: readonly EnrollmentLite[],
  fromMarathonId: string,
): TransferTarget[] {
  const from = marathons.find((m) => m.id === fromMarathonId);
  const subtype = from?.subtype?.trim();
  if (!subtype) return [];
  const active = new Set(enrollments.filter((e) => e.status === 'active').map((e) => e.marathon_id));
  return marathons
    .filter((m) => m.id !== fromMarathonId && !m.is_template && (m.subtype ?? '').trim() === subtype)
    .map((m) => ({ id: m.id, title: m.title, hasEnrollment: active.has(m.id) }))
    .sort((a, b) => Number(b.hasEnrollment) - Number(a.hasEnrollment) || a.title.localeCompare(b.title, 'ru'));
}

/**
 * Источники переноса: активные записи ученика в курсах с непустым подтипом. Снятую запись переносить нечем — сервер
 * ответит `no_source_enrollment`.
 */
export function transferSources(
  marathons: readonly Marathon[],
  enrollments: readonly EnrollmentLite[],
): Marathon[] {
  const byId = new Map(marathons.map((m) => [m.id, m]));
  const out: Marathon[] = [];
  for (const e of enrollments) {
    const m = byId.get(e.marathon_id);
    if (e.status === 'active' && m?.subtype?.trim() && !out.some((x) => x.id === m.id)) out.push(m);
  }
  return out;
}

/**
 * Группы строк отчёта → ключ подписи. Сервер отдаёт машинное имя таблицы; «points_logs:lesson» с двоеточием ключом
 * i18next быть не может (двоеточие — разделитель пространства), поэтому ключи свои.
 *
 * Порядок — как у сервера (`enrollment_transfer_rows.sql`): 10 групп отчёта; ниже — группы сверки и отката.
 */
export const TRANSFER_TABLE_KEYS: Record<string, string> = {
  answers: 'adminTransfer.tAnswers',
  audio_answer_durations: 'adminTransfer.tAudio',
  answer_field_attempts: 'adminTransfer.tFieldAttempts',
  section_progress: 'adminTransfer.tSections',
  homework_submissions: 'adminTransfer.tHomework',
  lesson_progress: 'adminTransfer.tLessons',
  lesson_manual_actions: 'adminTransfer.tManual',
  lesson_grade_history: 'adminTransfer.tGrades',
  'points_logs:lesson': 'adminTransfer.tPointsLesson',
  'points_logs:block': 'adminTransfer.tPointsBlock',
  points_logs: 'adminTransfer.tPoints',
  marathon_enrollments: 'adminTransfer.tEnrollments',
  'users.marathon_enrollments': 'adminTransfer.tEnrollments',
};

/** Подпись группы; незнакомое имя — как есть: лучше машинное слово, чем пустая строка. */
export function transferTableLabel(table: string, t: (key: string) => string): string {
  const key = TRANSFER_TABLE_KEYS[table];
  return key ? t(key) : table;
}

/** Код отказа или нарушенного условия → ключ текста (§9 договора). */
export const TRANSFER_CODE_KEYS: Record<string, string> = {
  student_not_found: 'adminTransfer.errStudentNotFound',
  course_not_found: 'adminTransfer.errCourseNotFound',
  transfer_not_found: 'adminTransfer.errTransferNotFound',
  actor_not_admin: 'adminTransfer.errActorNotAdmin',
  same_course: 'adminTransfer.errSameCourse',
  expect_required: 'adminTransfer.errExpectRequired',
  subtype_mismatch: 'adminTransfer.errSubtypeMismatch',
  no_source_enrollment: 'adminTransfer.errNoSource',
  no_target_enrollment: 'adminTransfer.errNoTarget',
  target_not_empty: 'adminTransfer.errTargetNotEmpty',
  unpaired_rows: 'adminTransfer.errUnpaired',
  rows_changed: 'adminTransfer.errRowsChanged',
  transfer_in_progress: 'adminTransfer.errInProgress',
  mirror_running: 'adminTransfer.errMirrorRunning',
  concurrent_change: 'adminTransfer.errConcurrent',
  transfer_guard_failed: 'adminTransfer.errGuardFailed',
  already_rolled_back: 'adminTransfer.errAlreadyRolledBack',
  not_latest_transfer: 'adminTransfer.errNotLatest',
  source_changed: 'adminTransfer.errSourceChanged',
  rows_moved: 'adminTransfer.errRowsMoved',
  target_has_new_rows: 'adminTransfer.errTargetHasNewRows',
};

/** Ключ текста по коду; незнакомый код — общий текст (новый код сервера не должен давать пустую строку). */
export function transferCodeKey(code: string | undefined): string {
  return (code && TRANSFER_CODE_KEYS[code]) || 'adminTransfer.errGeneric';
}

/**
 * Текст отказа ручки переноса или отката. 403 без кода — общая охрана ролей шлюза и службы (менеджер).
 */
export function transferErrorKey(err: unknown): string {
  const code = apiErrorCode(err);
  if (code) return transferCodeKey(code);
  const status = (err as { status?: unknown } | null | undefined)?.status;
  return status === 403 ? 'adminTransfer.errForbidden' : 'adminTransfer.errGeneric';
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** Тело отказа (клиент API кладёт его в `raw`, см. `apiErrorCode.ts`). */
function errorBody(err: unknown): Record<string, unknown> | undefined {
  if (!isObject(err)) return undefined;
  if (isObject(err.raw)) return err.raw;
  const response = err.response;
  if (isObject(response) && isObject(response.data)) return response.data;
  return undefined;
}

/**
 * Отчёт из отказа: пробный прогон с нарушенными условиями и `rows_changed` приходят не 200, а 409 с `report` —
 * таблицу чисел и строки без пары показывать всё равно нужно, иначе админ видит «нельзя» без «почему».
 */
export function errorReport(err: unknown): TransferReport | null {
  const body = errorBody(err);
  if (!body || !isObject(body.report)) return null;
  return body.report as unknown as TransferReport;
}

/** Нарушенные условия из отказа (`problems`) — коды §3. */
export function errorProblems(err: unknown): string[] {
  const body = errorBody(err);
  const list = body?.problems ?? (isObject(body?.report) ? (body!.report as Record<string, unknown>).problems : undefined);
  return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
}

/** Строки, мешающие откату (`rows_moved`, `target_has_new_rows`): таблица и число. */
export function errorRows(err: unknown): { table: string; rows: number }[] {
  const rows = errorBody(err)?.rows;
  if (!Array.isArray(rows)) return [];
  return rows.filter(isObject).map((r) => ({ table: String(r.table ?? ''), rows: Number(r.rows ?? 0) }));
}

/**
 * Названия курсов для итога «перенесён с … на …» — из ответа сервера, а не из списка «Откуда».
 *
 * ⚠️ После переноса запись-источник снята, и из списка источников курс пропадает: итог, собранный по списку, писал
 * «перенесён с «»» (стенд 28.09.2026). Список — только запасной вариант, если сервер название не отдал.
 */
export function doneTitles(
  report: Pick<TransferReport, 'from' | 'to'>,
  fallbackFrom: string,
  fallbackTo: string,
): { from: string; to: string } {
  return { from: report.from?.title || fallbackFrom, to: report.to?.title || fallbackTo };
}

/** Пробный прогон чистый: условий не нарушено, строк без пары нет, отпечаток есть. Только тогда — «Перенести». */
export function canRunTransfer(report: TransferReport | null): boolean {
  return !!report && !!report.expect && (report.problems ?? []).length === 0 && (report.unpaired ?? []).length === 0;
}

/**
 * Все рабочие курсы: сервер отдаёт не больше 100 за раз, а без подтипа нужного курса цель не найдётся — список
 * дочитывается страницами. Предел страниц — от бесконечного цикла, если `total` врёт.
 */
export async function listAllMarathonsPaged(
  fetchPage: (offset: number, limit: number) => Promise<{ items: Marathon[]; total: number }>,
  maxPages = 20,
): Promise<Marathon[]> {
  const out: Marathon[] = [];
  for (let page = 0; page < maxPages; page++) {
    const { items, total } = await fetchPage(page * 100, 100);
    out.push(...(items ?? []));
    if (!items?.length || out.length >= total) break;
  }
  return out;
}
